import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../../lib/api';
import { getSocket } from '../../lib/socket';
import { useAuthStore, useUIStore } from '../../store';
import { useConfirm } from '../ConfirmDialogProvider';
import type { GroupConversation, GroupMessage } from '../../types/conversations';

interface GroupChatThreadProps {
  group: GroupConversation;
}

interface MentionableUser {
  id: string;
  name: string;
  email: string;
}

// Real group chat thread replacing the Paket 1 "coming soon"
// placeholder. Adapts TaskDetails.tsx's message panel logic
// (fetch/send/edit/delete, @mention state machine, socket live
// updates) with its own independent state — TaskDetails.tsx is
// neither imported nor modified. Text-only: group messages have
// NO image attachment support on the backend, so none is added.
const GroupChatThread: React.FC<GroupChatThreadProps> = ({ group }) => {
  const groupId = group.id;
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const currentUser = useAuthStore((s) => s.user);
  const confirm = useConfirm();

  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [mentionableUsers, setMentionableUsers] = useState<MentionableUser[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageText, setEditingMessageText] = useState('');
  const [messageActionLoading, setMessageActionLoading] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionEnd, setMentionEnd] = useState(-1);
  const [mentionIndex, setMentionIndex] = useState(0);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const res = await api.get(`/workspace/groups/${groupId}/messages`);
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setMessages(data);
    } catch {
      // non-critical
    } finally {
      setMessagesLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    api
      .get(`/workspace/groups/${groupId}/mentionable-users`)
      .then((res) => {
        const data = (res.data as any)?.data;
        if (Array.isArray(data)) setMentionableUsers(data);
      })
      .catch(() => {
        // Non-critical — @mention autocomplete just won't offer anyone.
      });
  }, [groupId]);

  const getAuthorName = (authorId: string): string => {
    if (authorId === currentUser?.id) return t('globalChat.you');
    const m = mentionableUsers.find((m) => m.id === authorId);
    return m ? m.name : authorId;
  };

  const formatTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return dateStr;
    }
  };

  const canModifyMessage = (msg: GroupMessage): boolean =>
    !!currentUser && msg.author_id === currentUser.id;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('join_group', groupId);
    const handleMessageAdded = (payload: { message: GroupMessage; entityId: string; authorId: string; timestamp: string }) => {
      if (payload.entityId !== groupId) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.message.id)) return prev;
        return [...prev, payload.message];
      });
    };
    socket.on('group_message_added', handleMessageAdded);
    return () => {
      socket.emit('leave_group', groupId);
      socket.off('group_message_added', handleMessageAdded);
    };
  }, [groupId]);

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return mentionableUsers
      .filter((m) => m.id !== currentUser?.id && m.name?.trim())
      .filter((m) => m.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, mentionableUsers, currentUser]);

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessageText(value);
    const caret = e.target.selectionStart ?? value.length;
    let atIndex = -1;
    for (let i = caret - 1; i >= 0 && caret - i <= 30; i--) {
      const char = value[i];
      if (char === '@') {
        const before = i === 0 ? ' ' : value[i - 1];
        if (/\s/.test(before)) atIndex = i;
        break;
      }
      if (char === '\n') break;
    }
    if (atIndex === -1) {
      setMentionQuery(null);
      setMentionStart(-1);
      setMentionEnd(-1);
      return;
    }
    setMentionStart(atIndex);
    setMentionEnd(caret);
    setMentionQuery(value.slice(atIndex + 1, caret));
    setMentionIndex(0);
  };

  const insertMention = (member: MentionableUser) => {
    const before = messageText.slice(0, mentionStart);
    const after = messageText.slice(mentionEnd);
    const inserted = `@${member.name.trim()} `;
    const nextText = before + inserted + after;
    setMessageText(nextText);
    setMentionQuery(null);
    setMentionStart(-1);
    setMentionEnd(-1);
    const pos = before.length + inserted.length;
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
      messageInputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const handleMessageKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mentionQuery === null || mentionCandidates.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((i) => (i + 1) % mentionCandidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(mentionCandidates[mentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMentionQuery(null);
      setMentionStart(-1);
      setMentionEnd(-1);
    }
  };

  const extractMentionedUserIds = (text: string): string[] => {
    const ids: string[] = [];
    let scratch = text;
    const byLongestName = [...mentionableUsers]
      .filter((m) => m.id !== currentUser?.id && m.name?.trim())
      .sort((a, b) => b.name.trim().length - a.name.trim().length);
    for (const m of byLongestName) {
      const token = `@${m.name.trim()}`;
      if (scratch.includes(token)) {
        ids.push(m.id);
        scratch = scratch.split(token).join(' ');
      }
    }
    return Array.from(new Set(ids));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = messageText.trim();
    if (!text) return;
    setMentionQuery(null);
    const mentionedIds = extractMentionedUserIds(text);
    setSendingMessage(true);
    setMessageError(null);
    try {
      const res = await api.post(`/workspace/groups/${groupId}/messages`, {
        message: text,
        mentioned_user_ids: mentionedIds.length ? mentionedIds : undefined
      });
      const created = (res.data as any)?.data as GroupMessage | undefined;
      if (created) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === created.id)) return prev;
          return [...prev, created];
        });
      } else {
        await fetchMessages();
      }
      setMessageText('');
    } catch (err: any) {
      setMessageError(getApiErrorMessage(err, 'Failed to send message.'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartEditMessage = (msg: GroupMessage) => {
    setEditingMessageId(msg.id);
    setEditingMessageText(msg.message || '');
    setMessageError(null);
  };

  const handleCancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingMessageText('');
  };

  const handleSaveEditMessage = async (messageId: string) => {
    if (!editingMessageText.trim()) return;
    setMessageActionLoading(messageId);
    setMessageError(null);
    try {
      const res = await api.patch(`/workspace/groups/${groupId}/messages/${messageId}`, {
        message: editingMessageText.trim()
      });
      const updated = (res.data as any)?.data;
      if (updated) {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? updated : m)));
      } else {
        await fetchMessages();
      }
      setEditingMessageId(null);
      setEditingMessageText('');
    } catch (err: any) {
      setMessageError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setMessageActionLoading(null);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!(await confirm(t('globalChat.deleteConfirm'), { variant: 'danger' }))) return;
    setMessageActionLoading(messageId);
    setMessageError(null);
    try {
      await api.delete(`/workspace/groups/${groupId}/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (err: any) {
      setMessageError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setMessageActionLoading(null);
    }
  };

  // RENDER_A: message list (bubbles, inline edit, hover edit/delete).
  // flex-1/min-h-0 rather than a fixed height so this fills whatever
  // height the caller gives it (a short fixed box in the header
  // dropdown, the full remaining page height on the dedicated
  // /messages page) — the caller controls height via its own wrapper.
  const msgList = (
    <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-900/40">
      {messagesLoading && messages.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center py-6 text-sm">{t('common.loading')}</p>
      )}
      {!messagesLoading && messages.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center py-6 text-sm">{t('globalChat.noMessages')}</p>
      )}
      {messages.length > 0 && (
        <div className="space-y-2.5">
          {messages.map((msg) => {
            const isMine = msg.author_id === currentUser?.id;
            return (
              <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`group max-w-[85%] px-3 py-1.5 rounded-lg ${
                  isMine ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {editingMessageId === msg.id ? (
                    <div className="space-y-2">
                      <textarea
                        rows={2}
                        value={editingMessageText}
                        onChange={(e) => setEditingMessageText(e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEditMessage(msg.id)}
                          disabled={messageActionLoading === msg.id || !editingMessageText.trim()}
                          className={`px-3 py-1 rounded text-xs transition-colors disabled:opacity-50 ${isMine ? 'bg-white text-blue-600 hover:bg-blue-50' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                        >
                          {t('globalChat.save')}
                        </button>
                        <button
                          onClick={handleCancelEditMessage}
                          disabled={messageActionLoading === msg.id}
                          className={`px-3 py-1 border rounded text-xs transition-colors disabled:opacity-50 ${isMine ? 'border-blue-200 text-blue-100 hover:bg-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                          {t('globalChat.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm">{msg.message}</p>
                  )}
                  <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <p className={`text-xs whitespace-nowrap opacity-75 ${isMine ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                      {!isMine && `${getAuthorName(msg.author_id)} · `}{formatTime(msg.created_at)}{msg.updated_at ? ` ${t('globalChat.edited')}` : ''}
                    </p>
                    {!editingMessageId && canModifyMessage(msg) && (
                      <button
                        onClick={() => handleStartEditMessage(msg)}
                        disabled={messageActionLoading === msg.id}
                        className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 ${isMine ? 'text-blue-100 hover:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'}`}
                        title={t('globalChat.edit') as string}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    {!editingMessageId && canModifyMessage(msg) && (
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        disabled={messageActionLoading === msg.id}
                        className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 ${isMine ? 'text-blue-100 hover:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'}`}
                        title={t('globalChat.delete') as string}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>
      )}
    </div>
  );
  // RENDER_B_BELOW: composer with @mention dropdown + send button
  const composer = (
    <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
      {messageError && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-2 text-sm">{messageError}</div>
      )}
      <form onSubmit={handleSendMessage} className="flex gap-2 p-3">
        <div className="relative flex-1">
          <input
            ref={messageInputRef}
            type="text"
            value={messageText}
            onChange={handleMessageChange}
            onKeyDown={handleMessageKeyDown}
            placeholder={t('globalChat.placeholder')}
            title={t('globalChat.mentions.hint') as string}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={sendingMessage}
            role="combobox"
            aria-autocomplete="list"
            aria-controls="mention-suggestions"
            aria-expanded={mentionQuery !== null && mentionCandidates.length > 0}
          />
          {mentionQuery !== null && mentionCandidates.length > 0 && (
            <ul
              id="mention-suggestions"
              role="listbox"
              aria-label={t('globalChat.mentions.listLabel') as string}
              className="absolute bottom-full mb-1 left-0 w-full max-h-48 overflow-y-auto z-20 rounded-lg border shadow-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            >
              {mentionCandidates.map((m, i) => (
                <li key={m.id} role="option" aria-selected={i === mentionIndex}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setMentionIndex(i)}
                    onClick={() => insertMention(m)}
                    className={`w-full text-left px-3 py-2 text-sm ${i === mentionIndex ? 'bg-blue-50 dark:bg-gray-700' : ''}`}
                  >
                    <span className="font-medium text-gray-900 dark:text-gray-100">{m.name}</span>{' '}
                    <span className="text-xs text-gray-500 dark:text-gray-400">{m.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="submit"
          disabled={sendingMessage || !messageText.trim()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
        >
          {sendingMessage ? (
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {msgList}
      {composer}
    </div>
  );
};

export default GroupChatThread;